/**
 * 来源追踪 Store
 * 管理应用来源、渠道、营销参数等信息
 */

import { ref } from 'vue'
import type { IStorage } from '../storage/Storage'
import { STORAGE_KEYS } from '../../constants/storage-keys'
import { BaseStore } from './BaseStore'

export interface SourceState {
  source: string
  qhclickid: string | null
  bdVid: string | null
  strategy: string | null
  mp: string | null
  clueToken: string | null
}

export interface SourceConfig {
  source?: string
  qhclickid?: string
  bdVid?: string
  strategy?: string
  mp?: string
  clueToken?: string
}

export class SourceStore extends BaseStore<SourceState> {
  private _source = ref<string>('default')
  private _qhclickid = ref<string | null>(null)
  private _bdVid = ref<string | null>(null)
  private _strategy = ref<string | null>(null)
  private _mp = ref<string | null>(null)
  private _clueToken = ref<string | null>(null)

  constructor(storage: IStorage) {
    super(storage, STORAGE_KEYS.SOURCE)
    this.hydrate()
  }

  /**
   * 从存储中恢复状态（兼容旧版 key）
   */
  hydrate(): void {
    super.hydrate()

    let changed = false
    if (typeof localStorage !== 'undefined') {
      const readLegacy = (key: string): string | null => {
        try {
          const value = localStorage.getItem(key)
          if (!value) return null
          try {
            return JSON.parse(value)
          } catch {
            return value
          }
        } catch {
          return null
        }
      }

      const legacySource = readLegacy('source')
      if ((!this._source.value || this._source.value === 'default') && legacySource) {
        this._source.value = String(legacySource)
        changed = true
      }

      const legacyQhclickid = readLegacy('qhclickid')
      if (!this._qhclickid.value && legacyQhclickid) {
        this._qhclickid.value = String(legacyQhclickid)
        changed = true
      }

      const legacyBdVid = readLegacy('bdVid') || readLegacy('bd_vid')
      if (!this._bdVid.value && legacyBdVid) {
        this._bdVid.value = String(legacyBdVid)
        changed = true
      }

      const legacyStrategy = readLegacy('strategy')
      if (!this._strategy.value && legacyStrategy) {
        this._strategy.value = String(legacyStrategy)
        changed = true
      }

      const legacyMp = readLegacy('mp')
      if (!this._mp.value && legacyMp) {
        this._mp.value = String(legacyMp)
        changed = true
      }

      // 兼容旧版 clueToken（头条回传数据）
      const legacyClueToken = readLegacy('clueToken')
      if (!this._clueToken.value && legacyClueToken) {
        this._clueToken.value = String(legacyClueToken)
        changed = true
      }

      // 兼容旧版 invite_id
      const legacyInviteId = readLegacy('invite_id')
      if (legacyInviteId && !this.storage.getItem(STORAGE_KEYS.INVITE_ID)) {
        const parsedInviteId = parseInt(String(legacyInviteId), 10)
        this.storage.setItem(
          STORAGE_KEYS.INVITE_ID,
          Number.isNaN(parsedInviteId) ? legacyInviteId : parsedInviteId
        )
      }
    }

    if (changed) {
      this.persist()
    }
  }

  // Getters
  get source(): string {
    return this._source.value
  }

  get qhclickid(): string | null {
    return this._qhclickid.value
  }

  get bdVid(): string | null {
    return this._bdVid.value
  }

  get strategy(): string | null {
    return this._strategy.value
  }

  get mp(): string | null {
    return this._mp.value
  }

  get clueToken(): string | null {
    return this._clueToken.value
  }

  // Setters
  set source(value: string) {
    this._source.value = value
    this.persist()
  }

  set qhclickid(value: string | null) {
    this._qhclickid.value = value
    this.persist()
  }

  set bdVid(value: string | null) {
    this._bdVid.value = value
    this.persist()
  }

  set strategy(value: string | null) {
    this._strategy.value = value
    this.persist()
  }

  set mp(value: string | null) {
    this._mp.value = value
    this.persist()
  }

  set clueToken(value: string | null) {
    this._clueToken.value = value
    this.persist()
  }

  /**
   * 从 URL 参数初始化
   */
  initializeFromUrl(url: string): void {
    try {
      const urlObj = new URL(url, window.location.origin)
      const paramsMap: Record<string, string> = {}
      urlObj.searchParams.forEach((value, key) => {
        paramsMap[key] = value
      })
      if (urlObj.hash) {
        const hashQueryIndex = urlObj.hash.indexOf('?')
        if (hashQueryIndex !== -1) {
          const hashQuery = urlObj.hash.substring(hashQueryIndex + 1)
          const hashParams = new URLSearchParams(hashQuery)
          hashParams.forEach((value, key) => {
            paramsMap[key] = value
          })
        }
      }

      // 解析 from/source 参数
      const from = paramsMap.from || paramsMap.source
      if (from) {
        this._source.value = from
      }

      // 解析其他参数
      const qhclickid = paramsMap.qhclickid
      if (qhclickid) {
        this._qhclickid.value = qhclickid
      }

      const bdVid = paramsMap.bd_vid || paramsMap.bdVid
      if (bdVid) {
        this._bdVid.value = bdVid
      }

      const strategy = paramsMap.strategy
      if (strategy) {
        this._strategy.value = strategy
      }

      const mp = paramsMap.mp
      if (mp) {
        this._mp.value = mp
      }

      // 解析 clue_token（头条回传数据）
      const clueToken = paramsMap.clue_token
      if (clueToken) {
        this._clueToken.value = clueToken
      }

      // 解析 invite_id（存储到独立 key，供请求拦截器使用）
      const inviteId = paramsMap.invite_id
      if (inviteId) {
        const parsedInviteId = parseInt(inviteId, 10)
        this.storage.setItem(
          STORAGE_KEYS.INVITE_ID,
          Number.isNaN(parsedInviteId) ? inviteId : parsedInviteId
        )
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('invite_id', inviteId)
          }
        } catch {
          // ignore
        }
      }

      this.persist()
    } catch (error) {
      console.error('[SourceStore] Failed to initialize from URL:', error)
    }
  }

  /**
   * 初始化来源信息
   */
  initialize(config: SourceConfig): void {
    if (config.source) {
      this._source.value = config.source
    }
    if (config.qhclickid) {
      this._qhclickid.value = config.qhclickid
    }
    if (config.bdVid) {
      this._bdVid.value = config.bdVid
    }
    if (config.strategy) {
      this._strategy.value = config.strategy
    }
    if (config.mp) {
      this._mp.value = config.mp
    }
    if (config.clueToken) {
      this._clueToken.value = config.clueToken
    }
    this.persist()
  }

  /**
   * 获取来源参数（用于 API 请求）
   */
  getSourceParams(): Record<string, string> {
    const params: Record<string, string> = {
      source: this._source.value
    }
    if (this._qhclickid.value) {
      params.qhclickid = this._qhclickid.value
    }
    if (this._bdVid.value) {
      params.bdVid = this._bdVid.value
    }
    if (this._strategy.value) {
      params.strategy = this._strategy.value
    }
    if (this._mp.value) {
      params.mp = this._mp.value
    }
    if (this._clueToken.value) {
      params.clueToken = this._clueToken.value
    }
    return params
  }

  /**
   * 序列化状态
   */
  serialize(): string {
    return JSON.stringify({
      source: this._source.value,
      qhclickid: this._qhclickid.value,
      bdVid: this._bdVid.value,
      strategy: this._strategy.value,
      mp: this._mp.value,
      clueToken: this._clueToken.value
    })
  }

  /**
   * 反序列化状态
   */
  deserialize(data: string): void {
    try {
      const state = JSON.parse(data) as SourceState
      this._source.value = state.source || 'default'
      this._qhclickid.value = state.qhclickid || null
      this._bdVid.value = state.bdVid || null
      this._strategy.value = state.strategy || null
      this._mp.value = state.mp || null
      this._clueToken.value = state.clueToken || null
    } catch (error) {
      console.error('[SourceStore] Failed to deserialize state:', error)
    }
  }

  /**
   * 获取当前状态
   */
  getState(): SourceState {
    return {
      source: this._source.value,
      qhclickid: this._qhclickid.value,
      bdVid: this._bdVid.value,
      strategy: this._strategy.value,
      mp: this._mp.value,
      clueToken: this._clueToken.value
    }
  }

  /**
   * 清除所有数据
   */
  clear(): void {
    this._source.value = 'default'
    this._qhclickid.value = null
    this._bdVid.value = null
    this._strategy.value = null
    this._mp.value = null
    this._clueToken.value = null
    super.clear()

    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem('source')
        localStorage.removeItem('qhclickid')
        localStorage.removeItem('bdVid')
        localStorage.removeItem('strategy')
        localStorage.removeItem('mp')
        localStorage.removeItem('clueToken')
        localStorage.removeItem('invite_id')
      } catch {
        // ignore
      }
    }
  }

  /**
   * 持久化状态到存储（同时写入旧版 key）
   */
  persist(): void {
    super.persist()

    if (typeof localStorage === 'undefined') return
    const setOrRemove = (key: string, value: string | null) => {
      if (value === null || value === undefined || value === '') {
        localStorage.removeItem(key)
      } else {
        localStorage.setItem(key, value)
      }
    }

    setOrRemove('source', this._source.value || 'default')
    setOrRemove('qhclickid', this._qhclickid.value)
    setOrRemove('bdVid', this._bdVid.value)
    setOrRemove('strategy', this._strategy.value)
    setOrRemove('mp', this._mp.value)
    setOrRemove('clueToken', this._clueToken.value)
  }
}
